import React, {RefObject} from "react";
import {LoadingOutlined, UnorderedListOutlined} from "@ant-design/icons";
import ITimeTracker from "../../../../model/interface/extension/ITimeTracker";
import TimeTrackerService from "../../../../model/service/dataStorage/extension/TimeTrackerService";
import moment, {Moment} from "moment";
import {DATE_FORMAT_YYYY_MM_DD_HH_mm_ss} from "../../../../model/constants/DateConstant";
import {Badge, Divider, Form as AntFrom, FormInstance, Input, Modal, Popover, Row, Timeline, Tooltip} from "antd";
import Button from "../../../shared/button/Button";
import {MomentBuilder} from "../../../../utils/MomentBuilder";
import {connect} from "react-redux";
import IUser from "../../../../model/interface/security/IUser";
import IconBuilder from "../../../../utils/IconBuilder";
import SettingsService from "../../../../model/service/SettingsService";
import {FORM_RESERVED_NAMES} from "../../../../model/interface/form/IForm";
import Form from "../../form/Form";
import IBaseProps from "../../../../model/interface/IBaseProps";
import selectors from "../../../../redux/selectors";
import IRoute from "../../../../model/interface/dataStorage/IRoute";
import DataStorageHelper from "../../../../utils/DataStorageHelper";
import {humanTime} from "../../file-manager/utils/TimeDuration";
import {IAppState} from "../../../../redux/store";
import actions from "../../../../redux/actions";
import {IStreamEventsState} from "../../../../redux/reducers/StreamEvents";
import LocaleText from "../../settings/dictionary/LocaleText";


interface IState {
    loading: boolean
    openForm: boolean
    currentTime: Moment
}

interface IProps extends IBaseProps {
    user: IUser,
    current?: ITimeTracker,
    initialLoading: boolean
    isMobile: boolean
    findRouteByUuid: (uuid: string) => IRoute
    update: (changes: Partial<IStreamEventsState>) => void
    getText: (code: string, fallBack?: string) => string
}

class Trigger extends React.Component<IProps, IState> {

    formRef: RefObject<FormInstance> = React.createRef()
    timerID: undefined|number

    constructor(props: IProps) {
        super(props);
        this.state = {
            loading: false,
            openForm: false,
            currentTime: moment()
        }
    }

    componentDidMount() {
        this.timerID = setInterval(
            () => this.tick(),
            60000
        ) as any;
    }

    componentWillUnmount() {
        clearInterval(this.timerID);
    }

    tick() {
        this.setState({
            currentTime: moment()
        });
    }

    start = () => {
        const slaveContentType = TimeTrackerService.getInstance().getSlaveContentType()
        this.formRef.current?.validateFields().then(values => {
            this.setState({loading: true})
            TimeTrackerService.getInstance().collectionCreate({
                ...(slaveContentType ? DataStorageHelper.buildFormData(values, slaveContentType) : values)
            }).then((time) => {
                this.props.update({timeTracker: time})
                this.setState({loading: false, openForm: false})
            }).catch(() => this.setState({loading: false}))
        })
    }

    end = () => {
        const {current} = this.props
        this.setState({loading: true, openForm: false})
        current && TimeTrackerService.getInstance().resourceUpdate(current.uuid, {
            endAt: moment().format(DATE_FORMAT_YYYY_MM_DD_HH_mm_ss)
        })
            .then(() => {
                this.setState({loading: false})
                this.props.update({timeTracker: undefined})
            }).catch(() => this.setState({loading: false}))
    }

    setOpenForm = () => {
        this.setState(state => ({openForm: !state.openForm}))
    }

    render() {
        const {history, findRouteByUuid, isMobile, current, initialLoading, getText} = this.props
        const {loading, openForm, currentTime} = this.state
        const iconName = SettingsService.getValue('timeTracker', 'play-icon') || 'ClockCircleOutlined'
        const iconStopName = SettingsService.getValue('timeTracker', 'stop-icon') || 'ClockStopOutlined'
        const reportHeaderLink = SettingsService.isEnabled('timeTracker', 'header-report-link')
        const reportRoute = SettingsService.getValue('timeTracker', 'report-route')
        const start = current && MomentBuilder.build(current.startAt)
        const timeElapsed = start ? currentTime.diff(start, 'm') : 0

        return !initialLoading && current ? (
            <Popover visible={loading ? false : undefined} placement={'bottom'} title={current.name} content={<div>
                <Timeline pending={
                    <Button size={"small"} loading={loading} onClick={this.end} type={'danger'}
                            icon={IconBuilder(iconStopName)}>
                        <LocaleText code={'system.time-tracker.stop'} fallback={'Zastavití času'}/>
                    </Button>
                }>
                    <Timeline.Item color="green">
                        {start?.format('lll')}
                    </Timeline.Item>
                </Timeline>
            </div>}>
                <Tooltip placement={'left'} title={'Zastavit sledování času'}>
                    <Badge count={<span className={'ant-badge-count'} style={{zIndex: 1}}>
                        {timeElapsed > 48 * 60 ?
                            humanTime(timeElapsed, 'minute', ["day"], getText) :
                            Math.floor(timeElapsed / 60) + ":" +  String(Math.round(timeElapsed % 60)).padStart(2, '0')}
                    </span>} >
                        {IconBuilder(loading ? 'LoadingOutlined' : iconStopName, undefined, {
                            className: 'nav-icon cursor-pointer m-0',
                            onClick: this.end
                        })}
                    </Badge>
                </Tooltip>
            </Popover>

        ) : (<Row align={"middle"}>
                <div>
                    <Modal visible={openForm} title={<LocaleText code={'system.time-tracker.run.tooltip'} fallback={'Spustit sledování času'}/>} footer={[
                        <Button key={'run'} size={"small"} loading={loading} onClick={() => this.start()}
                                type={'success'}
                                icon={IconBuilder(iconName)}>
                            <LocaleText code={'system.time-tracker.run'} fallback={'Spustit'}/>
                        </Button>
                    ]} onCancel={this.setOpenForm}>
                        <div>
                            {this.getForm()}
                        </div>
                    </Modal>
                    <Tooltip title={<LocaleText code={'system.time-tracker.run.tooltip'} fallback={'Spustit sledování času'}/>} placement={'bottom'}>
                        <div>
                            {loading || initialLoading ? <LoadingOutlined className={'nav-icon'}/> :
                                IconBuilder(iconName, undefined, {
                                    className: 'cursor-pointer nav-icon',
                                    onClick: this.setOpenForm
                                })}
                        </div>
                    </Tooltip>
                </div>
                {reportHeaderLink && reportRoute && (
                    <>
                    {isMobile && <Divider type={'vertical'}/>}
                        <Tooltip title={<LocaleText code={'system.time-tracker.list'} fallback={'Výkaz sledování času'}/>} placement={'bottom'}>
                            <div>
                                <UnorderedListOutlined className={'cursor-pointer m-0 nav-icon'}
                                                       onClick={() => history.push(findRouteByUuid(reportRoute).url)}/>
                            </div>
                        </Tooltip>
                    </>
                )}
            </Row>
        )
    }

    getForm = () => {
        const {history, match, getText} = this.props
        const form = TimeTrackerService.getInstance().getSlaveContentTypeForm(FORM_RESERVED_NAMES.TIME_TRACKER)
        if (form) {
            return <Form history={history} match={match} form={form} formRef={this.formRef} values={{}}
                         onSubmit={this.start}/>
        }

        return <AntFrom ref={this.formRef} onFinish={this.start}>
            <AntFrom.Item name={'name'}
                          rules={[{required: true, message: <LocaleText code={'system.time-tracker.name.required'} fallback={'Vyplňte prosím název činnosti!'}/>}]}>
                <Input placeholder={getText('system.time-tracker.name.required', 'název činnosti').toLowerCase()}/>
            </AntFrom.Item>
        </AntFrom>
    }
}

const mapStateToProps = (state: IAppState) => {
    const {user} = state.setup
    const {timeTracker, initialLoading} = state.streamEvents
    return {
        user,
        current: timeTracker,
        initialLoading: initialLoading,
        findRouteByUuid: (uuid: string) => selectors.routes.findOneBy(state, 'uuid', uuid),
        getText: (code: string, fallback?: string) => selectors.dictionary.getMessage(state, code, fallback)
    }
}

const mapDispatchToProps = {
    update: actions.streamEvents.update
}

export default connect(mapStateToProps, mapDispatchToProps)(Trigger)